/**
 * The pure half of `<SearchableSelect>` (newsletter-tag-audience,
 * 2026-10-02): which options a search keeps, and where the arrow keys move
 * the highlight. Kept out of the component so the SPA's DOM-less test runner
 * can pin it (`searchable-select.test.ts`).
 */

export interface SearchableSelectOption {
  /** What `onChange` receives — e.g. a tag's slug. */
  readonly value: string;
  /** What the list and the closed dropdown show. */
  readonly label: string;
  /** Muted text at the row's right — e.g. how many members carry the tag. */
  readonly meta?: string;
  /** More text a search matches without showing it — e.g. the slug. */
  readonly keywords?: string;
}

/** Lower case, accents dropped, runs of whitespace as one space. */
export function normalizeSearchText(text: string): string {
  return text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/\s+/gu, " ")
    .trim();
}

/**
 * The options a search keeps, in their original order: every word typed must
 * appear somewhere in the label or the keywords, so "source insta" finds
 * "Source: Instagram". An empty search keeps them all.
 */
export function filterSearchableOptions<T extends SearchableSelectOption>(
  options: ReadonlyArray<T>,
  query: string,
): ReadonlyArray<T> {
  const words = normalizeSearchText(query).split(" ").filter((w) => w.length > 0);
  if (words.length === 0) return options;
  return options.filter((o) => {
    const haystack = normalizeSearchText(`${o.label} ${o.keywords ?? ""}`);
    return words.every((w) => haystack.includes(w));
  });
}

/**
 * The highlighted row after an arrow key: one down or up, wrapping at the
 * ends. `-1` when there is nothing to highlight; from `-1`, down lands on the
 * first row and up on the last.
 */
export function moveSearchableActiveIndex(
  current: number,
  step: 1 | -1,
  length: number,
): number {
  if (length <= 0) return -1;
  if (current < 0 || current >= length) return step === 1 ? 0 : length - 1;
  return (current + step + length) % length;
}
